'use client'

import type { Route } from 'next'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthModal } from '../auth/auth-modal-provider'
import { onAuthTokenChange, readStoredToken } from '../auth/auth-storage'

interface Props {
  children: React.ReactNode
  /** Test seam — defaults to reading from localStorage. */
  getTokenFn?: () => string | null
  /**
   * Where to land once the in-place sign-in succeeds, forwarded straight to
   * `useAuthModal().open()`:
   *  - `null` (default) → STAY on the current page (refresh only). The right
   *    default for pages the user deliberately deep-linked into — /app/account/team,
   *    /app/referral, /admin — so signing in doesn't bounce them away.
   *  - a path → navigate there after sign-in.
   *
   * `/app/account` overrides this per-request (see `resolveAccountPostAuthRedirect`)
   * so an INCIDENTAL landing (the navbar 帳號 link, shown for a possibly-stale
   * token) defaults to the transcripts home instead of stranding the user on
   * 帳單與用量.
   */
  redirectTo?: Route | null
}

/**
 * Client-side gate: if no bearer token is in localStorage, open the shared
 * sign-in modal IN PLACE (no navigation) and render a placeholder. We don't
 * validate the token here — server responses are still the authoritative
 * gate; this is just a UX prompt so the dashboard doesn't render skeleton
 * state for signed-out viewers.
 *
 * On successful sign-in `storeToken` fires `AUTH_TOKEN_CHANGE_EVENT`, which
 * flips `hasToken` true and reveals the children — the user stays on the page
 * (the modal opens with `redirectTo: null`), no bounce to a separate /login.
 *
 * The auto-open only fires for the INITIAL arrival check (mounted with no
 * token at all) — never for a later true→false transition (this tab signing
 * out, or a background 401 self-heal evicting a dead token). Popping the
 * modal the instant a user signs out (or gets silently evicted) is jarring;
 * the placeholder below still renders and offers a manual way back in.
 */
export function AuthGuard({ children, getTokenFn = readStoredToken, redirectTo = null }: Props) {
  const { t } = useTranslation('web')
  const { open } = useAuthModal()
  const [hasToken, setHasToken] = useState<boolean | null>(null)
  // Set once the first check (mount) has run, so later checks (triggered by
  // AUTH_TOKEN_CHANGE_EVENT) know they are a TRANSITION, not an arrival.
  const hasCheckedInitial = useRef(false)

  useEffect(() => {
    const check = () => {
      const next = Boolean(getTokenFn())
      setHasToken(next)
      if (!hasCheckedInitial.current) {
        hasCheckedInitial.current = true
        // Only an arrival with no token auto-opens the modal.
        if (!next) open({ redirectTo })
      }
    }
    check()
    // Re-check when sign-in/out writes the token in this tab.
    return onAuthTokenChange(check)
  }, [getTokenFn, open, redirectTo])

  if (hasToken === null) return null
  if (!hasToken) {
    // The modal auto-opened above; this also gives a way BACK in if the
    // user dismissed it (these self-chrome routes have no navbar, so
    // without it a dismissed modal would dead-end on the placeholder).
    return (
      <div className="center-note" style={{ display: 'grid', gap: 12, justifyItems: 'center' }}>
        <p className="note">{t('common.signInToContinue')}</p>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => open({ redirectTo })}
        >
          {t('auth.modalTriggerCta')}
        </button>
      </div>
    )
  }
  return <>{children}</>
}
